import { type IframeHTMLAttributes, useMemo } from "react";
import { EMBED_IFRAME_ALLOW, EMBED_SHELL_SANDBOX } from "../constants";
import { extractDirectIframeSrc } from "../utils/direct-iframe";
import { getEmbedShellUrl } from "../utils/embed-shell";

type SandboxedHtmlProps = {
  /** Customer-supplied HTML. Never rendered into the host document. */
  code: string;
  title: string;
} & Pick<
  IframeHTMLAttributes<HTMLIFrameElement>,
  "className" | "tabIndex" | "loading"
>;

/**
 * Renders untrusted embed HTML without ever executing it on the host origin.
 * Use this instead of `dangerouslySetInnerHTML` / `createContextualFragment`
 * for anything a customer can type into the editor.
 *
 * - A snippet that is just an inert `<iframe src="https://…">` (YouTube, Vimeo,
 *   Loom, Calendly, …) becomes a plain cross-origin iframe.
 * - Everything else is handed to the embed shell on the assets origin (see
 *   `EMBED_SHELL_PATH`). The embed then runs on that origin, isolated from the
 *   host DOM, cookies and the Firebase session, while nested providers keep
 *   working because they are no longer under an opaque origin.
 */
export const SandboxedHtml = ({
  code,
  title,
  className,
  tabIndex,
  loading,
}: SandboxedHtmlProps) => {
  const { src, isShell } = useMemo(() => {
    if (!code) return { src: "", isShell: false };
    const direct = extractDirectIframeSrc(code);
    return direct
      ? { src: direct, isShell: false }
      : { src: getEmbedShellUrl(code), isShell: true };
  }, [code]);

  // Keyed by its URL: the shell carries the code in the fragment, and a
  // fragment-only src change is an in-page jump the shell never re-renders
  // for. Edited code (the agent refining a component) stayed on screen as the
  // old version until a reload.
  return (
    <iframe
      key={src}
      src={src}
      sandbox={isShell ? EMBED_SHELL_SANDBOX : undefined}
      allow={EMBED_IFRAME_ALLOW}
      allowFullScreen
      title={title}
      className={className}
      tabIndex={tabIndex}
      loading={loading}
    ></iframe>
  );
};
